import { useEffect, useRef, useState } from 'react';

interface TypedConfirmModalProps {
  open: boolean;
  title: string;
  description?: string;
  /** The literal string the operator must type verbatim to enable confirm. */
  requiredText: string;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  busy?: boolean;
}

/**
 * "Type EXACTLY to confirm" modal (#344). Used for high-blast-radius AppConfig
 * toggles where a single errant click would page the team: enabling
 * maintenance mode, flipping the global kill switch, raising minimumVersion,
 * or disabling an entire ad format. Requiring a verbatim match is proven
 * friction — significantly reduces accidental mutations versus a one-click
 * confirm.
 */
export default function TypedConfirmModal({
  open,
  title,
  description,
  requiredText,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
  busy,
}: TypedConfirmModalProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [value, setValue] = useState('');

  useEffect(() => {
    if (!open) {
      setValue('');
      return;
    }
    inputRef.current?.focus();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onCancel();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onCancel, busy]);

  if (!open) return null;

  const canConfirm = !busy && value === requiredText;

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="typed-confirm-title"
      data-testid="typed-confirm-modal"
    >
      <div
        className="absolute inset-0 bg-slate-900/50"
        onClick={() => {
          if (!busy) onCancel();
        }}
      />
      <div className="relative bg-white rounded-lg shadow-lg max-w-md w-full p-5 space-y-4">
        <div>
          <h2
            id="typed-confirm-title"
            className="text-base font-semibold text-slate-900"
          >
            {title}
          </h2>
          {description && (
            <p className="text-sm text-slate-600 mt-1">{description}</p>
          )}
        </div>

        <div className="space-y-1">
          <p className="text-sm text-slate-700">
            Type{' '}
            <code className="bg-slate-100 px-1.5 py-0.5 rounded text-red-700 font-mono">
              {requiredText}
            </code>{' '}
            to confirm.
          </p>
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={requiredText}
            disabled={busy}
            data-testid="typed-confirm-input"
            className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
          />
        </div>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            data-testid="typed-confirm-cancel"
            className="text-sm border border-slate-300 rounded px-3 py-1.5 hover:bg-slate-50 disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={() => {
              if (canConfirm) onConfirm();
            }}
            disabled={!canConfirm}
            data-testid="typed-confirm-confirm"
            className="text-sm rounded px-3 py-1.5 bg-red-600 text-white hover:bg-red-500 disabled:opacity-50"
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
